import { useEffect, useMemo, useRef, useState, type ChangeEvent } from "react";
import { Button } from "@/components/ui/button";
import { Download, Loader2, Plus, Upload } from "lucide-react";
import { toast } from "sonner";
import { toastApiError } from "@/lib/toastApiError";
import { createTermMultipart } from "@/lib/termsApi";
import type { Term } from "../types";
import {
  AdminTermsTable,
  type AdminTermsServerPagination,
} from "../components/AdminTermsTable";
import { WordMasterImportMappingDialog } from "../components/WordMasterImportMappingDialog";
import {
  applyWordMasterMapping,
  downloadBlob,
  exportTermsToXlsxBlob,
  inspectWordMasterWorkbook,
  type WordMasterColumnMapping,
} from "../wordMasterExcel";

export interface AdminWordMasterPageProps {
  terms: Term[];
  isLoading: boolean;
  serverPagination: AdminTermsServerPagination;
  onAdd: () => void;
  onEdit: (term: Term) => void;
  onDelete: (term: Term) => void;
  canDeleteTerms: boolean;
  onStatusChange: (
    term: Term,
    next: "pending" | "approved" | "rejected"
  ) => void;
  onRefresh: () => void;
}

export function AdminWordMasterPage({
  terms,
  isLoading,
  serverPagination,
  onAdd,
  onEdit,
  onDelete,
  canDeleteTerms,
  onStatusChange,
  onRefresh,
}: AdminWordMasterPageProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [importing, setImporting] = useState(false);
  const [mappingDialogOpen, setMappingDialogOpen] = useState(false);
  const [pendingImportRows, setPendingImportRows] = useState<
    Record<string, unknown>[]
  >([]);
  const [pendingExcelColumns, setPendingExcelColumns] = useState<string[]>([]);

  const [exportSelectionIds, setExportSelectionIds] = useState<Set<string>>(
    () => new Set()
  );

  useEffect(() => {
    const valid = new Set(terms.map(t => t.id));
    setExportSelectionIds(prev => {
      let pruned = false;
      const next = new Set<string>();
      for (const id of prev) {
        if (valid.has(id)) next.add(id);
        else pruned = true;
      }
      return pruned ? next : prev;
    });
  }, [terms]);

  const selectedTermsForExport = useMemo(
    () => terms.filter(t => exportSelectionIds.has(t.id)),
    [terms, exportSelectionIds]
  );

  const handleExport = () => {
    const list = selectedTermsForExport;
    if (list.length === 0) return;
    const blob = exportTermsToXlsxBlob(list);
    const stamp = new Date().toISOString().slice(0, 10);
    downloadBlob(blob, `lexicon-word-master-${stamp}.xlsx`);
    toast.success(
      `Exported ${list.length} selected term${list.length === 1 ? "" : "s"}`
    );
  };

  const handleImportFileChosen = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;

    try {
      const buffer = await file.arrayBuffer();
      const { columns, rows } = inspectWordMasterWorkbook(buffer);
      if (columns.length === 0) {
        toastApiError(
          "No columns found. Use the first sheet with a header row."
        );
        return;
      }
      setPendingExcelColumns(columns);
      setPendingImportRows(rows);
      setMappingDialogOpen(true);
    } catch (err) {
      console.error(err);
      toastApiError("Could not read the spreadsheet file.");
    }
  };

  const runImportWithMapping = async (mapping: WordMasterColumnMapping) => {
    const rowsSnapshot = pendingImportRows;
    setMappingDialogOpen(false);
    setPendingExcelColumns([]);
    setPendingImportRows([]);

    if (!mapping.word) {
      toastApiError('Map the "Word" field to a spreadsheet column before importing.');
      return;
    }

    const parsed = applyWordMasterMapping(rowsSnapshot, mapping);
    if (parsed.length === 0) {
      toastApiError(
        "No importable rows (each row needs a non-empty value in the mapped Word column)."
      );
      return;
    }

    setImporting(true);
    try {
      let ok = 0;
      const failures: string[] = [];

      for (const row of parsed) {
        const { ok: created, error } = await createTermMultipart(
          {
            word: row.word,
            meaning_english: row.meaning_english,
            meaning_hindi: row.meaning_hindi,
            meaning_gujarati: row.meaning_gujarati,
            pronunciation: row.pronunciation,
            example_usage: row.example_usage,
            status: row.status,
            contributor_name: row.contributor_name,
            contributor_email: row.contributor_email,
            is_featured: row.is_featured,
            is_enabled: row.is_enabled,
            ...(row.admin_notes.trim()
              ? { admin_notes: row.admin_notes }
              : {}),
          },
          null,
          "staff"
        );
        if (created) {
          ok += 1;
        } else {
          failures.push(`${row.word}: ${error ?? "Unknown error"}`);
        }
      }

      if (ok > 0) {
        toast.success(
          `Imported ${ok} of ${parsed.length} row${parsed.length === 1 ? "" : "s"}`
        );
        onRefresh();
      }
      if (failures.length > 0) {
        const preview = failures.slice(0, 5).join(" · ");
        toastApiError(
          failures.length === parsed.length
            ? `Import failed for all rows. ${preview}`
            : `${failures.length} row${failures.length === 1 ? "" : "s"} failed. ${preview}`
        );
        console.warn("[Word Master import]", failures);
      }
    } catch (err) {
      console.error(err);
      toastApiError("Import failed unexpectedly.");
    } finally {
      setImporting(false);
    }
  };

  const busy = isLoading || importing;

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="mb-1 font-serif text-2xl font-bold">Word Master</h2>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={busy || exportSelectionIds.size === 0}
            onClick={handleExport}
            className="gap-1.5"
            title={
              exportSelectionIds.size === 0
                ? "Select one or more rows in the table to export"
                : undefined
            }
          >
            <Download className="h-4 w-4 shrink-0" />
            Export Excel
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={busy || mappingDialogOpen}
            onClick={() => fileInputRef.current?.click()}
            className="gap-1.5"
          >
            {importing ? (
              <Loader2 className="h-4 w-4 shrink-0 animate-spin" />
            ) : (
              <Upload className="h-4 w-4 shrink-0" />
            )}
            Import Excel
          </Button>
          <input
            ref={fileInputRef}
            type="file"
            accept=".xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
            className="hidden"
            onChange={handleImportFileChosen}
          />
          <Button onClick={onAdd} className="bg-primary hover:bg-primary/90">
            <Plus className="mr-2 h-4 w-4" /> Add New Term
          </Button>
        </div>
      </div>
      <AdminTermsTable
        terms={terms}
        status="all"
        isLoading={isLoading}
        serverPagination={serverPagination}
        showFeatured
        showDeleteActions={canDeleteTerms}
        rowSelection={{
          selectedIds: exportSelectionIds,
          onChange: setExportSelectionIds,
        }}
        onEdit={onEdit}
        onDelete={onDelete}
        onStatusChange={onStatusChange}
        onRefresh={onRefresh}
      />
      <WordMasterImportMappingDialog
        open={mappingDialogOpen}
        onOpenChange={open => {
          setMappingDialogOpen(open);
          if (!open) {
            setPendingImportRows([]);
            setPendingExcelColumns([]);
          }
        }}
        excelColumns={pendingExcelColumns}
        rawRowCount={pendingImportRows.length}
        onConfirm={runImportWithMapping}
      />
    </div>
  );
}
